import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatUbicacion } from '../utils/locationUtils';
import { Calendar, HardDrive, CheckCircle, XCircle, FileCheck, Trash2, Building, Mail } from 'lucide-react';
import { FileText, User, Eye, ArrowLeft, Upload, Download, Clock, MapPin, Phone, ChevronRight } from '../components/icons';
import SignatureDrawer from '../components/SignatureDrawer';
import { asignacionesAPI, actasAPI, consumiblesAPI } from '../services/api';
import { elegirHistoricaDeAsignacion } from '../utils/actasMatch';
import GenerarActaModal from '../components/GenerarActaModal';
import ActaActions from '../components/ActaActions';
import Tooltip from '../components/Tooltip';
import StatusBadge from '../components/StatusBadge';

const ActaDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const { user } = useAuth();

    // Estados principales
    const [asignacion, setAsignacion] = useState(null);
    const [acta, setActa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados de modales
    const [showGenerarActaModal, setShowGenerarActaModal] = useState(false);
    const [selectedAsignacion, setSelectedAsignacion] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [observaciones, setObservaciones] = useState('');
    const [selectedAsignacionForUpload, setSelectedAsignacionForUpload] = useState(null);

    // Estados para modal de previsualización personalizada
    const [showPrevisualizacionModal, setShowPrevisualizacionModal] = useState(false);
    const [selectedActaForPreview, setSelectedActaForPreview] = useState(null);
    const [selectedAsignacionForPreview, setSelectedAsignacionForPreview] = useState(null);
    const [incluirFirmaTI, setIncluirFirmaTI] = useState(true);
    const [fechaEntrega, setFechaEntrega] = useState('');

    // Estados para aprobación/rechazo
    const [showAprobarModal, setShowAprobarModal] = useState(false);
    const [showRechazarModal, setShowRechazarModal] = useState(false);
    const [comentariosAprobacion, setComentariosAprobacion] = useState('');
    const [aprobarLoading, setAprobarLoading] = useState(false);
    const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const handleSaveSignatureFromDrawer = async (signatureFile) => {
        try {
            const formData = new FormData();
            formData.append('firma', signatureFile);
            await actasAPI.subirFirma(formData);
            showToast('Éxito', 'Firma digital registrada correctamente en tu perfil', 'success');
            setShowSignatureDrawer(false);

            if (pendingAction) {
                const actionToRetry = pendingAction;
                setPendingAction(null);
                await actionToRetry();
            }
        } catch (err) {
            console.error('Error guardando firma:', err);
            showToast('Error', 'No se pudo guardar la firma digital', 'error');
        }
    };

    const checkSignatureErrorAndPrompt = (error, actionCallback) => {
        const isNoSig =
            error.response?.data?.code === 'NO_SIGNATURE' ||
            (error.response?.status === 400 && (error.response?.data?.message || '').toLowerCase().includes('firma'));

        if (isNoSig) {
            const msg = error.response?.data?.message || 'No tienes una firma digital cargada en tu perfil. Por favor dibuja tu firma a continuación.';
            showToast('Firma Requerida', msg, 'info');
            if (actionCallback) setPendingAction(() => actionCallback);
            setShowSignatureDrawer(true);
            return true;
        }
        return false;
    };

    useEffect(() => {
        // Validación básica de ID
        if (!id) {
            showToast('Error', 'No se ha seleccionado una asignación válida', 'error');
            navigate('/gestion-actas', { replace: true });
            setLoading(false);
            return;
        }
        fetchActaData(id);
    }, [id]);

    const fetchActaData = async (paramId) => {
        try {
            setLoading(true);
            setError(null);

            const isMovimiento = typeof paramId === 'string' && paramId.startsWith('mov_');
            const numericId = isMovimiento ? Number(paramId.replace('mov_', '')) : Number(paramId);

            if (Number.isNaN(numericId)) {
                throw new Error('ID inválido');
            }

            if (isMovimiento) {
                // Modo Insumos
                const response = await consumiblesAPI.getMovimiento(numericId);
                const mov = response.data;

                // Mapeo
                setAsignacion({
                    id: `mov_${mov.id}`,
                    tipo: 'consumible',
                    fechaAsignacion: mov.fecha,
                    estado: 'Entregado',
                    activo: {
                        codigo: `MOV-${mov.id}`,
                        categoria: mov.consumible.categoria || 'Insumos',
                        nombre: mov.consumible.nombre
                    },
                    usuario: {
                        nombre: mov.usuario?.nombre,
                        apellido: mov.usuario?.apellido,
                        email: mov.usuario?.email,
                        empresa: mov.usuario?.empresa || 'N/A',
                        ubicacion: mov.usuario?.ubicacion || 'N/A'
                    }
                });

                // Acta
                try {
                    const actaRes = await actasAPI.getByMovimientoId(numericId);
                    setActa(actaRes.data || null);
                } catch (e) {
                    setActa(null);
                }

            } else {
                // Modo Activos (Asignaciones)
                const asignacionRes = await asignacionesAPI.getById(numericId);
                const asig = asignacionRes.data;
                setAsignacion(asig);

                let actaEncontrada = null;
                try {
                    const actaRes = await actasAPI.getByAsignacionId(numericId);
                    actaEncontrada = actaRes.data || null;
                } catch (e) {
                    actaEncontrada = null;
                }

                // Si la asignación no tiene un acta propia, buscar un acta histórica del mismo equipo
                // y de la MISMA persona (por id o por nombre). Las de otros dueños del equipo no cuentan.
                if (!actaEncontrada) {
                    const activoId = asig?.activoId ?? asig?.activo?.id;
                    if (activoId) {
                        try {
                            const { data: historicas } = await actasAPI.getActasByActivo(activoId);
                            actaEncontrada = elegirHistoricaDeAsignacion(asig, Array.isArray(historicas) ? historicas : []);
                        } catch (e) {
                            // sin históricas para este activo
                        }
                    }
                }

                setActa(actaEncontrada);
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);
            setError('No se pudo cargar la información. El recurso podría no existir o no tienes permisos.');
            showToast('Error', 'No se pudo cargar la información detallada', 'error');
            // Quitamos el navigate automático para que el usuario pueda ver el error
            // o intentar recargar.
        } finally {
            setLoading(false);
        }
    };

    // Funciones de acciones
    const handleGenerarActa = (asignacion) => {
        setSelectedAsignacion(asignacion);
        setShowGenerarActaModal(true);
    };

    const handleCloseGenerarActa = () => {
        setShowGenerarActaModal(false);
        setSelectedAsignacion(null);
    };

    const handleGenerarActaSuccess = () => {
        setShowGenerarActaModal(false);
        setSelectedAsignacion(null);
        fetchActaData(id); // Reload using the route param
        showToast('Éxito', 'Acta generada correctamente', 'success');
    };

    const handleMarcarPendienteFirma = async (asignacionId) => {
        try {
            const isMov = typeof asignacionId === 'string' && asignacionId.startsWith('mov_');
            const numericId = isMov ? Number(asignacionId.replace('mov_', '')) : Number(asignacionId);

            const payload = isMov ? { movimientoId: numericId } : { asignacionId: numericId };

            await actasAPI.marcarPendienteFirma(payload);
            showToast('Éxito', 'Acta marcada como pendiente de firma', 'success');
            fetchActaData(id);
        } catch (error) {
            console.error('Error al marcar como pendiente:', error);
            showToast('Error', 'No se pudo marcar como pendiente', 'error');
        }
    };

    const handleSubirActaUsuario = (asignacion) => {
        setSelectedAsignacionForUpload(asignacion);
        setShowUploadModal(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadActa = async () => {
        if (!selectedFile) {
            showToast('Error', 'Por favor selecciona un archivo', 'error');
            return;
        }
        const asignacionIdVal = selectedAsignacionForUpload?.id;

        // Handle mov_ prefix
        const isMov = typeof asignacionIdVal === 'string' && asignacionIdVal.startsWith('mov_');
        const numericId = isMov ? Number(asignacionIdVal.replace('mov_', '')) : Number(asignacionIdVal);

        if (asignacionIdVal === undefined || asignacionIdVal === null || asignacionIdVal === '' || Number.isNaN(numericId)) {
            showToast('Error', 'La asignación seleccionada no es válida', 'error');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('acta', selectedFile);

            // Backend might expect asignacionId or movimientoId depending on implementation.
            // Assuming subirActaAdmin can handle logic or we send both/one.
            // If backend strictly validates asignacionId for assignments, uploading for insumos might need backend change.
            // For now, let's send 'asignacionId' as the numeric ID but if it's a movement, we might need a flag.
            // Checking ActasController would be ideal but simpler to try:
            if (isMov) {
                formData.append('movimientoId', numericId.toString());
            } else {
                formData.append('asignacionId', numericId.toString());
            }
            // Add flag just in case backend checks
            formData.append('esInsumo', isMov.toString());

            formData.append('observaciones', observaciones);

            await actasAPI.subirActaAdmin(formData);

            setShowUploadModal(false);
            setSelectedFile(null);
            setObservaciones('');
            setSelectedAsignacionForUpload(null);

            showToast('Éxito', 'Acta subida correctamente', 'success');
            fetchActaData(id); // Recargar datos usando el id del hook Params
        } catch (error) {
            console.error('Error al subir acta:', error);
            showToast('Error', 'No se pudo subir el acta', 'error');
        } finally {
            setUploading(false);
        }
    };

    // 🎯 FLUJO COMPLETO - 6 CONDICIONES DEL OJO AZUL
    const handlePrevisualizarActa = async (acta, idGenerico) => {
        try {
            let response;
            let asignacionId = null;
            let movimientoId = null;

            if (typeof idGenerico === 'string' && idGenerico.startsWith('mov_')) {
                movimientoId = parseInt(idGenerico.replace('mov_', ''));
            } else {
                asignacionId = parseInt(idGenerico);
            }

            // 1. 🆕 PENDIENTE DE FIRMA - !acta (no existe acta)
            if (!acta) {
                if (movimientoId) {
                    response = await actasAPI.previsualizarMovimiento(movimientoId);
                } else {
                    response = await actasAPI.previsualizarActa(asignacionId);
                }
                showToast('Información', 'Generando acta sin firma para el usuario', 'info');
            }
            // 2. ⏳ PENDIENTE DE APROBACIÓN
            else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'pdf_subido') {
                response = await actasAPI.descargarActa(acta.id);
                showToast('Información', 'Revisando PDF subido por el usuario', 'info');
            }
            // 3. ✍️ FIRMADA DIGITALMENTE
            else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'digital') {
                response = await actasAPI.previsualizarActaFirmado(acta.id);
                showToast('Información', 'Revisando acta con firma digital del usuario', 'info');
            }
            // 4. 📤 SUBIDA POR ADMIN/SOPORTE
            else if (acta.metodoFirma?.toLowerCase() === 'admin_subida') {
                response = await actasAPI.descargarActa(acta.id);
                showToast('Información', 'Revisando acta que subiste', 'info');
            }
            // Caso por defecto
            else {
                if (movimientoId) {
                    response = await actasAPI.previsualizarMovimiento(movimientoId);
                } else {
                    response = await actasAPI.previsualizarActa(asignacionId);
                }
                showToast('Información', 'Previsualizando acta', 'info');
            }

            const url = window.URL.createObjectURL(response.data);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error:', error);
            showToast('No se pudo previsualizar el acta', 'error');
        }
    };

    const handlePrevisualizacionPersonalizada = (acta, asignacion) => {
        setSelectedActaForPreview(acta);
        setSelectedAsignacionForPreview(asignacion);
        setIncluirFirmaTI(true);
        setFechaEntrega(new Date().toISOString().split('T')[0]);
        setShowPrevisualizacionModal(true);
    };

    const handleConfirmarPrevisualizacion = async () => {
        try {
            let response;
            let idParaPrevisualizar = null;
            let idGenerico = selectedAsignacionForPreview?.id || id;

            if (selectedActaForPreview) {
                idParaPrevisualizar = selectedActaForPreview.id;
            } else {
                idParaPrevisualizar = idGenerico;
            }

            // Usar la API personalizada (esta API acepta ID genérico y lo procesa en el backend)
            response = await actasAPI.previsualizarActaPersonalizada(idParaPrevisualizar, {
                incluirFirmaTI: incluirFirmaTI,
                fechaEntrega: fechaEntrega
            });

            const url = window.URL.createObjectURL(response.data);
            window.open(url, '_blank');

            setShowPrevisualizacionModal(false);
            setSelectedActaForPreview(null);
            setSelectedAsignacionForPreview(null);
        } catch (error) {
            console.error('Error:', error);
            if (!checkSignatureErrorAndPrompt(error, handleConfirmarPrevisualizacion)) {
                showToast('Error', 'No se pudo generar la previsualización personalizada', 'error');
            }
        }
    };

    const handleAprobarActa = async (actaId, aprobado, comentarios = '') => {
        try {
            setAprobarLoading(true);
            await actasAPI.aprobarActa(actaId, { aprobar: aprobado, comentarios: comentarios });

            const mensaje = aprobado ? 'Acta aprobada correctamente' : 'Acta rechazada correctamente';
            showToast('Éxito', mensaje, 'success');

            setShowAprobarModal(false);
            setShowRechazarModal(false);
            setComentariosAprobacion('');

            // Recargar datos para actualizar el estado
            const currentIdNum = Number(id);
            if (currentIdNum && !Number.isNaN(currentIdNum) && currentIdNum > 0) {
                fetchActaData(currentIdNum);
            }

            // También navegar de vuelta a la gestión de actas para ver los cambios
            setTimeout(() => {
                navigate('/gestion-actas');
            }, 1000);
        } catch (error) {
            console.error('Error al procesar acta:', error);
            showToast('Error', 'No se pudo procesar la acta', 'error');
        } finally {
            setAprobarLoading(false);
        }
    };

    const handleDescargarActa = async (actaId) => {
        try {
            const response = await actasAPI.descargarActa(actaId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `acta_${asignacion.activo?.codigo}_${asignacion.usuario?.nombre}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar acta:', error);
            showToast('Error', 'No se pudo descargar el acta', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Activa':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'Inactiva':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getEstadoActaColor = (estado) => {
        if (!estado) return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';

        const estadoLower = estado.toLowerCase();
        switch (estadoLower) {
            case 'pendiente':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'firmada':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'aprobada':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rechazada':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getCategoriaIcon = (categoria) => {
        switch (categoria?.toLowerCase()) {
            case 'laptop':
                return '💻';
            case 'desktop':
                return '🖥️';
            case 'monitor':
                return '🖥️';
            case 'teclado':
                return '⌨️';
            case 'mouse':
                return '🖱️';
            case 'impresora':
                return '🖨️';
            case 'scanner':
                return '📷';
            case 'tablet':
                return '📱';
            case 'celular':
                return '📱';
            case 'accesorio':
                return '🔌';
            default:
                return '💻';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !asignacion) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                Error al cargar el acta
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                {error || 'No se encontró la información del acta'}
                            </p>
                            <button
                                onClick={() => navigate(location.pathname.includes('/actas/') ? '/actas' : '/gestion-actas')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Volver a {location.pathname.includes('/actas/') ? 'Mis Actas' : 'Gestión de Actas'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate(location.pathname.includes('/actas/') ? '/actas' : '/gestion-actas')}
                            className="p-2.5 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-gray-700"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                                Detalle del Acta
                            </h1>
                            <div className="flex items-center mt-1.5 space-x-2">
                                <span className="text-slate-500 dark:text-slate-400 font-medium">
                                    {asignacion.activo?.codigo}
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="text-slate-500 dark:text-slate-400">
                                    {asignacion.activo?.categoria}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Información Principal */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    {/* Información del Activo */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 transition-all hover:shadow-md">
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900/40 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800">
                                <span className="text-xl">{getCategoriaIcon(asignacion.activo?.categoria)}</span>
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                    Información del Activo
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Datos técnicos del equipo</p>
                            </div>
                        </div>

                        <div className="space-y-5 mt-2">
                            <div className="group">
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Código de Inventario</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{asignacion.activo?.codigo}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Categoría</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{asignacion.activo?.categoria}</p>
                            </div>
                            {(asignacion.activo?.marca || asignacion.activo?.modelo) && (
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Marca / Modelo</label>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">
                                        {[asignacion.activo?.marca, asignacion.activo?.modelo].filter(Boolean).join(' ') || 'N/A'}
                                    </p>
                                </div>
                            )}
                            {(asignacion.activo?.nombreEquipo || asignacion.activo?.nombre) && (
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Nombre del Equipo</label>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{asignacion.activo?.nombreEquipo || asignacion.activo?.nombre}</p>
                                </div>
                            )}
                            {[
                                ['Número de Serie', asignacion.activo?.serie],
                                ['IMEI', asignacion.activo?.imei],
                                ['N° Celular', asignacion.activo?.numeroCelular],
                                ['Capacidad', asignacion.activo?.capacidad],
                                ['Procesador', asignacion.activo?.procesador],
                                ['RAM', asignacion.activo?.ram],
                                ['Almacenamiento', asignacion.activo?.almacenamiento],
                                ['Sistema Operativo', asignacion.activo?.sistemaOperativo],
                            ].filter(([, v]) => v != null && String(v).trim() !== '').map(([label, value]) => (
                                <div key={label}>
                                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{label}</label>
                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1 break-all">{value}</p>
                                </div>
                            ))}
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Estado Actual</label>
                                <div className="mt-1.5">
                                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold rounded uppercase tracking-wider ${getStatusColor(asignacion.estado)}`}>
                                        {asignacion.estado}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Información del Usuario */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 transition-all hover:shadow-md">
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900/40 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800">
                                <User className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                    Información del Usuario
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Responsable asignado</p>
                            </div>
                        </div>

                        <div className="space-y-5 mt-2">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Nombre Completo</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">
                                    {asignacion.usuario?.nombre} {asignacion.usuario?.apellido}
                                </p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Correo Corporativo</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1 truncate">{asignacion.usuario?.email}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Empresa / Entidad</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{asignacion.usuario?.empresa || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Ubicación / Oficina</label>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">{formatUbicacion(asignacion.usuario?.ubicacion) || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Estado del Acta */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 transition-all hover:shadow-md">
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900/40 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800">
                                <FileText className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                    Estado del Acta
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Progreso de la firma</p>
                            </div>
                        </div>

                        <div className="space-y-5 mt-2">
                            {acta ? (
                                <>
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Estatus de Firma</label>
                                        <div className="flex flex-col space-y-2 mt-1">
                                            <div>
                                                <StatusBadge estado={acta.estado} />
                                            </div>
                                            {acta.metodoFirma && (
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    Método: <span className="text-gray-700 dark:text-gray-300">{acta.metodoFirma.replace('_', ' ')}</span>
                                                </div>
                                            )}
                                            {acta.esHistorica && (
                                                <div>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                        Acta histórica
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {(acta.fechaFirma || acta.fechaCreacion) && (
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                                {acta.esHistorica ? 'Fecha de Entrega / Firma' : 'Fecha de Creación'}
                                            </label>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                                {new Date(acta.fechaFirma || acta.fechaCreacion).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                                            </p>
                                        </div>
                                    )}
                                    {acta.fechaSubida && (
                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de Subida</label>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                                {new Date(acta.fechaSubida).toLocaleDateString('es-ES')}
                                            </p>
                                        </div>
                                    )}
                                    {acta.nombreArchivo && (
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Archivo Digital</label>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{acta.nombreArchivo}</p>
                                        </div>
                                    )}
                                    {acta.subidoPor && (
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Subido Por</label>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-1">
                                                {acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin acta generada</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Acciones */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Acciones Disponibles
                    </h3>

                    <div className="space-y-4">
                        {/* Acciones TI (siempre visibles para admin/soporte) */}
                        {(user?.role === 'admin' || user?.role === 'soporte') && (
                            <div className="flex flex-wrap gap-3">
                                <Tooltip content="Generar acta (con opciones de firma TI y fecha)">
                                    <button
                                        onClick={() => handleGenerarActa(asignacion)}
                                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <FileCheck className="w-4 h-4" />
                                        <span>Generar Acta</span>
                                    </button>
                                </Tooltip>
                            </div>
                        )}

                        {/* Acciones del acta usando el componente centralizado */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                Acciones del Acta
                            </h4>
                            <ActaActions
                                acta={acta}
                                asignacion={asignacion}
                                // ActaActions suele llamar onActionComplete() sin argumentos.
                                // Este wrapper asegura que siempre se recargue usando el ID válido de la ruta.
                                onActionComplete={() => {
                                    const currentIdNum = Number(id);
                                    if (!Number.isNaN(currentIdNum)) {
                                        fetchActaData(currentIdNum);
                                    }
                                }}
                                onApprove={() => setShowAprobarModal(true)}
                                onReject={() => setShowRechazarModal(true)}
                                currentUserRole={user?.role}
                            />
                        </div>
                    </div>
                </div>

                {/* Información Adicional */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-8">
                    <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1.5 h-6 bg-slate-800 dark:bg-slate-500 rounded-full"></div>
                        <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                            Información Adicional
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                        <div className="group">
                            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Fecha de Asignación</label>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                {new Date(asignacion.fechaAsignacion).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </p>
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1.5">Estado Operativo</label>
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${getStatusColor(asignacion.estado)}`}>
                                {asignacion.estado}
                            </span>
                        </div>

                        {(asignacion?.asignadoPor || asignacion?.asignado_por || acta?.asignacion?.asignadoPor) && (
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Asignado Por</label>
                                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    {asignacion?.asignadoPor || asignacion?.asignado_por || acta?.asignacion?.asignadoPor}
                                </p>
                            </div>
                        )}

                        {acta && (
                            <>
                                <div className="border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">ID del Acta</label>
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{acta.id}</p>
                                </div>
                                <div className="border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                    <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1.5">Estado Documento</label>
                                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${getEstadoActaColor(acta.estado)}`}>
                                        📄 {acta.estado}
                                    </span>
                                </div>
                                {acta.nombreArchivo && (
                                    <div className="md:col-span-2 border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Nombre del Archivo</label>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">{acta.nombreArchivo}</p>
                                    </div>
                                )}
                                {acta.observaciones && acta.observaciones.includes('SHA256:') && (
                                    <div className="md:col-span-2 border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Hash de Integridad (SHA256)</label>
                                        <p className="text-xs font-mono break-all text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800 mt-1">
                                            {acta.observaciones.split('SHA256:')[1].replace(']', '').trim()}
                                        </p>
                                    </div>
                                )}
                                {acta.fechaSubida && (
                                    <div className="border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Fecha de Procesamiento</label>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            {new Date(acta.fechaSubida).toLocaleDateString('es-ES', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                )}
                                {acta.subidoPor && (
                                    <div className="border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Subido Por</label>
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            {acta.subidoPor.nombre ? `${acta.subidoPor.nombre} ${acta.subidoPor.apellido || ''}`.trim() : acta.subidoPor.username}
                                        </p>
                                    </div>
                                )}
                                {acta.observaciones && (
                                    <div className="md:col-span-2 border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Observaciones / Notas</label>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 italic">
                                            "{acta.observaciones.includes('SHA256:') ? acta.observaciones.split('[Hash')[0].trim() : acta.observaciones}"
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modales */}
            {showGenerarActaModal && (
                <GenerarActaModal
                    isOpen={showGenerarActaModal}
                    onClose={handleCloseGenerarActa}
                    asignacion={selectedAsignacion}
                    onSuccess={handleGenerarActaSuccess}
                />
            )}

            {/* Modal de Upload */}
            {showUploadModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowUploadModal(false);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Subir Acta Firmada
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Archivo PDF
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setSelectedFile(null);
                                    setObservaciones('');
                                    setSelectedAsignacionForUpload(null);
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleUploadActa}
                                disabled={uploading || !selectedFile}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                            >
                                {uploading ? 'Subiendo...' : 'Subir Acta'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de previsualización personalizada */}
            {showPrevisualizacionModal && createPortal(
                <div
                    className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowPrevisualizacionModal(false);
                        }
                    }}
                >
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                Previsualización Personalizada
                            </h3>
                            <div className="space-y-4">
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                                Personaliza la previsualización del acta en estado pendiente de firma.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Incluir firma de TI
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={incluirFirmaTI}
                                            onChange={(e) => {
                                                const newValue = e.target.checked;
                                                setIncluirFirmaTI(newValue);
                                            }}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                            Mostrar firma de TI en el documento
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Fecha de entrega
                                    </label>
                                    <input
                                        type="date"
                                        value={fechaEntrega}
                                        onChange={(e) => setFechaEntrega(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    />
                                </div>

                                <div className="flex justify-end space-x-2">
                                    <button
                                        onClick={() => {
                                            setShowPrevisualizacionModal(false);
                                            setSelectedActaForPreview(null);
                                            setSelectedAsignacionForPreview(null);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleConfirmarPrevisualizacion}
                                        className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
                                    >
                                        Previsualizar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Aprobar */}
            {showAprobarModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowAprobarModal(false);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Aprobar Acta
                        </h3>

                        <div className="space-y-4">
                            {/* Información del acta */}
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <p><strong>Activo:</strong> {asignacion?.activo?.codigo} - {asignacion?.activo?.categoria}</p>
                                    <p><strong>Usuario:</strong> {asignacion?.usuario?.nombre} {asignacion?.usuario?.apellido}</p>
                                    <p><strong>Estado actual:</strong> {acta?.estado}</p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Comentarios (opcional)
                                </label>
                                <textarea
                                    value={comentariosAprobacion}
                                    onChange={(e) => setComentariosAprobacion(e.target.value)}
                                    rows={3}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Comentarios adicionales..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAprobarModal(false);
                                    setComentariosAprobacion('');
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleAprobarActa(acta.id, true, comentariosAprobacion)}
                                disabled={aprobarLoading}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                {aprobarLoading ? 'Procesando...' : 'Aprobar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Rechazar */}
            {showRechazarModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowRechazarModal(false);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Rechazar Acta
                        </h3>

                        <div className="space-y-4">
                            {/* Información del acta */}
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <p><strong>Activo:</strong> {asignacion?.activo?.codigo} - {asignacion?.activo?.categoria}</p>
                                    <p><strong>Usuario:</strong> {asignacion?.usuario?.nombre} {asignacion?.usuario?.apellido}</p>
                                    <p><strong>Estado actual:</strong> {acta?.estado}</p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Comentarios (requerido)
                                </label>
                                <textarea
                                    value={comentariosAprobacion}
                                    onChange={(e) => setComentariosAprobacion(e.target.value)}
                                    rows={3}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Motivo del rechazo..."
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowRechazarModal(false);
                                    setComentariosAprobacion('');
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleAprobarActa(acta.id, false, comentariosAprobacion)}
                                disabled={aprobarLoading || !comentariosAprobacion.trim()}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {aprobarLoading ? 'Procesando...' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {/* Signature Drawer para usuarios/admins sin firma digital */}
            <SignatureDrawer
                isOpen={showSignatureDrawer}
                onClose={() => setShowSignatureDrawer(false)}
                onSave={handleSaveSignatureFromDrawer}
                title="Crear tu firma digital para actas"
            />
        </div>
    );
};

export default ActaDetail;
